import { Gift, Star, Crown, Check, ArrowRight } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../site/Motion";
import { SectionLabel } from "../site/Bits";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.renewtracker.app";

const plans = [
  {
    icon: Gift,
    tag: "Free",
    name: "Free",
    monthly: "₹0",
    period: "Free forever",
    altPrice: null,
    badge: "No card required",
    desc: "Get started and track your first renewals at no cost.",
    points: [
      "Track up to 5 documents",
      "Email reminders",
      "Push notifications",
      "Basic renewal tracking",
    ],
    featured: false,
  },
  {
    icon: Star,
    tag: "Starter",
    name: "Starter",
    monthly: "₹99",
    period: "/month",
    altPrice: "or ₹999/year",
    badge: null,
    desc: "For individuals and small businesses tracking more renewals.",
    points: [
      "Track up to 30 documents",
      "Advanced reminder options",
      "Email & push notifications",
      "Ideal for individuals & small teams",
    ],
    featured: false,
  },
  {
    icon: Crown,
    tag: "Business",
    name: "Business",
    monthly: "₹249",
    period: "/month",
    altPrice: "or ₹2,499/year",
    badge: "Best value",
    desc: "For businesses managing multiple renewals across the organisation.",
    points: [
      "Unlimited documents",
      "For businesses with many renewals",
      "Priority features & support",
      "Everything in Starter, and more",
    ],
    featured: true,
  },
];

export const Pricing = () => (
  <section id="pricing" className="relative scroll-mt-24 bg-white py-24" data-testid="pricing-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal className="mx-auto max-w-2xl text-center">
        <div className="flex justify-center">
          <SectionLabel>Pricing</SectionLabel>
        </div>
        <h2 className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-ink sm:text-5xl">
          Plans for every business — and individuals too.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          Start free, then upgrade as your renewals grow. Paid plans are purchased inside the RenewTracker Android app.
        </p>
      </Reveal>

      <StaggerGroup className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {plans.map((p) => (
          <StaggerItem key={p.name}>
            <div
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${
                p.featured ? "border-brand bg-brand text-white shadow-lg" : "border-slate-200 bg-background text-ink"
              }`}
            >
              {p.badge && !p.featured && (
                <span className="absolute right-6 top-6 rounded-full bg-greenaccent/15 px-3 py-1 text-[11px] font-semibold text-greenaccent-dark">
                  {p.badge}
                </span>
              )}
              {p.badge && p.featured && (
                <span className="absolute right-6 top-6 rounded-full bg-greenaccent px-3 py-1 text-[11px] font-semibold text-white">
                  {p.badge}
                </span>
              )}

              <span className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${p.featured ? "bg-white/15 text-white" : "bg-brand/5 text-brand"}`}>
                <p.icon className="h-3.5 w-3.5" /> {p.tag}
              </span>
              <h3 className="mt-5 font-display text-2xl font-bold">{p.name}</h3>
              <p className={`mt-2 text-sm ${p.featured ? "text-white/70" : "text-slate-600"}`}>{p.desc}</p>

              <div className={`mt-6 border-y py-5 ${p.featured ? "border-white/15" : "border-slate-200"}`} data-testid={`plan-price-${p.name.toLowerCase()}`}>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-display text-3xl font-bold">{p.monthly}</span>
                  <span className={`text-sm font-medium ${p.featured ? "text-white/70" : "text-slate-600"}`}>{p.period}</span>
                </div>
                {p.altPrice && (
                  <p className={`mt-1 text-xs font-medium ${p.featured ? "text-white/70" : "text-slate-500"}`}>
                    {p.altPrice}
                  </p>
                )}
              </div>

              <ul className="mt-6 flex-1 space-y-3">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-sm">
                    <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${p.featured ? "bg-white/15" : "bg-greenaccent/15"}`}>
                      <Check className={`h-3 w-3 ${p.featured ? "text-white" : "text-greenaccent"}`} />
                    </span>
                    <span className={p.featured ? "text-white/90" : "text-slate-700"}>{pt}</span>
                  </li>
                ))}
              </ul>

              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                aria-label={`Get RenewTracker on Google Play — ${p.name} plan`}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5 ${
                  p.featured
                    ? "bg-white text-brand hover:bg-white/90"
                    : "bg-ink text-white hover:bg-ink/90"
                }`}
              >
                {p.name === "Free" ? "Get the App" : "Get Started"}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>

      <Reveal delay={0.1} className="mx-auto mt-10 max-w-xl text-center">
        <p className="text-xs text-slate-500">
          Paid plans are managed through Google Play subscriptions inside the RenewTracker Android app.
        </p>
      </Reveal>
    </div>
  </section>
);

export default Pricing;
